import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'
import { withMask } from 'use-mask-input'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button, InputField } from '@/components/ui'
import { ComboboxField } from '@/components/ui/combobox'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/brasil/components/layouts'
import type { TProcuradorPropostaSchema } from '@/features/proposta/brasil/domain/schemas'
import type { CompradorVinculoStepSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import { useClientePorCpfProposta } from '@/features/proposta/brasil/hooks/use-cliente-por-cpf-proposta'
import { useDadosProcuradorVinculo } from '@/features/proposta/brasil/hooks/use-dados-procurador-vinculo'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import type { TNacionalidade, TProfissao } from '@/types/cliente'
import { CPF_MASK } from '@/utils/masks'

type TDadosComprador = {
  onBack: () => void
  onNext: () => void
  compradores: TCompradoresCadastrados
  procuradores: TProcuradorPropostaSchema[]
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
}

export const DadosProcuradorVinculo = ({
  onNext,
  onBack,
  compradores,
  nacionalidades,
  procuradores,
  profissoes,
}: TDadosComprador) => {
  const {
    formState: { errors },
    watch,
    control,
  } = useFormContext<CompradorVinculoStepSchemaInput>()

  const cpf = watch('dados.cpf')
  const buscouCpf = watch('dados.buscouCpf')
  const cpfRaw = cpf?.replaceAll(/\D/g, '')
  const isEditing = watch('mode') === 'edit'
  const nacionalidadePrincipalId = nacionalidades[0]?.id ?? ''

  const { refetch, isFetching } = useClientePorCpfProposta(String(cpfRaw), false)
  const { handlers } = useDadosProcuradorVinculo({
    onNext,
    compradores,
    nacionalidadePrincipalId,
    procuradores,
    refetch,
  })

  const prefix = 'proposta:cadastro_compradores.steps.dados_comprador'
  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t(`${prefix}.procurador_titulo`)}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="w-full">
          <h3 className="font-poppins neutral-800 mb-6 tracking-wide">{t(`${prefix}.subtitulo`)}</h3>
          <div className="mt-1 mb-4 grid max-w-114 grid-cols-[182px_1fr] items-end gap-4 md:grid-cols-[1fr_127px] lg:grid-cols-[362px_1fr] lg:gap-10">
            <InputField
              ref={withMask('cpf', CPF_MASK)}
              name="dados.cpf"
              control={control}
              type="text"
              label={t(`${prefix}.label.cpf`)}
              placeholder={t(`${prefix}.placeholder.cpf`)}
              disabled={buscouCpf || isEditing}
              afterContent={<Icon name="search" className="text-muted-foreground" />}
            />
            <Render.If condition={!isEditing}>
              {() => (
                <Render.If
                  condition={buscouCpf}
                  fallback={
                    <Button
                      isLoading={isFetching}
                      variant="outline"
                      loadingText=""
                      className="mb-7 border-gray-300 bg-white hover:bg-gray-50 sm:mb-9"
                      disabled={!cpfRaw || !!errors.dados?.cpf}
                      onClick={handlers.handleBuscarCliente}
                    >
                      {t(`${prefix}.buscar`)}
                    </Button>
                  }
                >
                  {() => (
                    <Button
                      isLoading={isFetching}
                      variant="ghost"
                      className="text-destructive hover:text-destructive mb-7 border-gray-300 bg-white sm:mb-9"
                      onClick={handlers.handleLimpar}
                    >
                      <span className="sm:hidden">{t(`${prefix}.limpar_mobile`)}</span>
                      <span className="hidden sm:inline">{t(`${prefix}.limpar`)}</span>
                    </Button>
                  )}
                </Render.If>
              )}
            </Render.If>
          </div>
          <div className="mb-6 border-t border-neutral-200" />
          <div className="relative flex flex-1 flex-col gap-2 md:grid md:grid-cols-1 md:gap-0">
            <InputField
              className="max-w-90"
              disabled={!buscouCpf}
              required
              control={control}
              name="dados.nome"
              label={t(`${prefix}.label.nome`)}
              placeholder={t(`${prefix}.placeholder.nome`)}
              maxLength={100}
            />
            <ComboboxField
              disabled={!buscouCpf}
              className="max-w-90"
              placeholder={t(`${prefix}.placeholder.selecione`)}
              name="dados.nacionalidadeId"
              defaultValue={nacionalidades[0]?.id}
              control={control}
              label={t(`${prefix}.label.nacionalidade`)}
              options={nacionalidades}
              valueProperty="id"
              labelProperty="descricao"
              hideClearButton
            />
            <ComboboxField
              disabled={!buscouCpf}
              className="max-w-90"
              placeholder={t(`${prefix}.placeholder.selecione`)}
              name="dados.profissaoId"
              control={control}
              label={t(`${prefix}.label.profissao`)}
              options={profissoes}
              valueProperty="id"
              labelProperty="descricao"
              hideClearButton
            />
          </div>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={onBack}>
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-auto" onClick={handlers.validateOnNext}>
          {t('proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
